import { useState } from "react";
import { type PaginationState } from "@tanstack/react-table";

import { DemandCompaniesTable } from "./DemandCompaniesTable";
import { Column, Row } from "@/components";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { useGetDemandCompanies } from "@/hooks/queries/useGetDemandCompanies";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";

interface DemandCompaniesProps {
  className?: string;
}

// This number looks the best to fit the screen and not have too much scrolling.
const PAGE_SIZE = 15;

export const DemandCompanies = ({ className }: DemandCompaniesProps) => {
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });

  const {
    data,
    isPending: isDemandCompaniesPending,
    isError: isDemandCompaniesError,
    isFetching: isDemandCompaniesFetching,
  } = useGetDemandCompanies({
    query,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
  });

  const companies = data?.companies ?? [];
  const totalPages = data?.totalPages;
  const hasMore = data?.hasMore ?? false;
  const currentPage = data?.page ?? pagination.pageIndex + 1;

  const handleSearch = () => {
    mixpanelUtils.trackFormSubmission("Demand search", MIXPANEL_SCREENS.DEMAND);
    setQuery(searchInput.trim());
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handlePreviousPage = () => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: Math.max(0, prev.pageIndex - 1),
    }));
  };

  const handleNextPage = () => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: prev.pageIndex + 1,
    }));
  };

  const canPreviousPage = pagination.pageIndex > 0;
  const canNextPage =
    typeof totalPages === "number"
      ? pagination.pageIndex < totalPages - 1
      : hasMore;
  const disablePrevious = isDemandCompaniesFetching || !canPreviousPage;
  const disableNext = isDemandCompaniesFetching || !canNextPage;

  const render = () => {
    return (
      <Column className={cn("flex-1 gap-4", className)}>
        <Row className="items-center justify-between">
          <Row className="w-full max-w-xl gap-2">
            <Input
              placeholder="Search companies..."
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleSearch();
                }
              }}
            />
            <Button
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  "Search Companies button",
                  MIXPANEL_SCREENS.DEMAND,
                );
                handleSearch();
              }}
            >
              Search
            </Button>
          </Row>
          {typeof data?.total === "number" ? (
            <p className="text-sm text-slate-500">
              {data.total.toLocaleString()}{" "}
              {data.total === 1 ? "company" : "companies"}
            </p>
          ) : null}
        </Row>

        <DemandCompaniesTable
          companies={companies}
          isDemandCompaniesPending={isDemandCompaniesPending}
          isDemandCompaniesError={isDemandCompaniesError}
        />

        <Row className="items-center justify-between">
          <p className="text-sm text-slate-600">
            {typeof totalPages === "number" && totalPages === 0
              ? "No results"
              : `Page ${String(currentPage)}${
                  typeof totalPages === "number"
                    ? ` of ${String(totalPages)}`
                    : ""
                }`}
          </p>
          <Row className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  "Pagination Previous",
                  MIXPANEL_SCREENS.DEMAND,
                );
                handlePreviousPage();
              }}
              disabled={disablePrevious}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  "Pagination Next",
                  MIXPANEL_SCREENS.DEMAND,
                );
                handleNextPage();
              }}
              disabled={disableNext}
            >
              Next
            </Button>
          </Row>
        </Row>
      </Column>
    );
  };

  return render();
};
